import AbstractApiService from 'src/services/abstractApiService';
import { api } from 'src/pages/APIs';
import type {
  WatchdogConfiguration,
  WatchdogConfigurationInput,
  XsltTemplate,
  XsltTemplateInput,
  WatchdogStatus,
  ConfigType,
  WatchdogExportType,
  RestApiFieldMapping,
  V2ConfigSchema,
  XsltValidationRequest,
  XsltValidationResponse,
  XsltDocType,
} from 'src/pages/Settings/Watchdog/types/watchdog.types';

/**
 * Backend API response for configurations
 * Maps backend field names to frontend types
 */
interface BackendConfiguration {
  id: string;
  name: string;
  config_type: string; // 'import' or 'export'
  sub_org_id: string | null;
  sub_org_name?: string;
  doc_type: string | null; // Backend uses 'doc_type', frontend uses 'doc_type_key'
  doc_type_name?: string;
  watch_folder: string | null; // Backend uses 'watch_folder', frontend uses 'import_path'
  success_folder: string | null; // Backend uses 'success_folder', frontend uses 'success_path'
  export_folder: string | null; // Backend uses 'export_folder', frontend uses 'export_path'
  xslt_template_id: string | null;
  xslt_template_name?: string;
  export_destination?: string[]; // Backend uses array, e.g., ['folder'], ['infor'], ['rest_api']
  export_config_id?: number | null;
  export_config_name?: string;
  // REST API fields
  rest_api_url?: string;
  method?: string;
  username?: string;
  password?: string;
  body_type?: string;
  rest_api_mapping?: RestApiFieldMapping;
  // GLS840MI fields
  endpoint_name?: string;
  // Webhook fields
  webhook_url?: string;
  // BOD mapping
  bod_mapping?: Record<string, string>;
  is_default: boolean;
  enabled: boolean; // Backend uses 'enabled', frontend uses 'is_active'
  created_at?: string;
  updated_at?: string;
}

/**
 * Backend API response for templates
 */
interface BackendTemplate {
  id: string;
  name: string;
  content: string;
  usage_count: number;
  used_by_configs: Array<{ id: string; name: string }>;
  created_at: string;
  updated_at: string;
}

/**
 * Backend API response wrapper for template list
 */
interface TemplateListApiResponse {
  success: boolean;
  templates: BackendTemplate[];
  message?: string;
}

/**
 * Backend API response wrapper for single template
 */
interface TemplateSingleApiResponse {
  success: boolean;
  template: BackendTemplate;
  message?: string;
}

/**
 * Backend API response wrapper for configuration list
 */
interface ConfigurationListApiResponse {
  success: boolean;
  configurations: BackendConfiguration[];
  message?: string;
}

/**
 * Backend API response wrapper for single configuration
 */
interface ConfigurationSingleApiResponse {
  success: boolean;
  configuration: BackendConfiguration;
  message?: string;
}

/**
 * Backend API response for V2 config import
 */
interface V2ConfigImportApiResponse {
  success: boolean;
  configurations: BackendConfiguration[];
  message?: string;
  created_count: number;
}

/**
 * WatchdogApiService - API service for WatchDog configuration management
 *
 * Provides methods for CRUD operations on WatchDog configurations and XSLT templates.
 * Communicates with the backend API endpoints defined in doc2-api/routers/watchdog/.
 */
export default class WatchdogApiService extends AbstractApiService {
  public constructor() {
    super({});
  }

  // ============================================
  // CONFIGURATION ENDPOINTS
  // ============================================

  /**
   * Get all configurations for the organization
   */
  public async getConfigurations(): Promise<WatchdogConfiguration[]> {
    const response = await super.get<void, ConfigurationListApiResponse>(
      api.watchdog_configurations,
    );
    return this.mapConfigurationsFromBackend(response.configurations || []);
  }

  /**
   * Create a new configuration
   */
  public async createConfiguration(
    input: WatchdogConfigurationInput,
  ): Promise<WatchdogConfiguration> {
    const backendInput = this.mapConfigurationToBackend(input);
    const response = await super.post<ConfigurationSingleApiResponse>(
      api.watchdog_configurations,
      backendInput,
    );

    // Check for API-level validation errors
    if (!response.success || !response.configuration) {
      throw new Error(response.message || 'Failed to create configuration');
    }

    return this.mapConfigurationFromBackend(response.configuration);
  }

  /**
   * Update an existing configuration
   */
  public async updateConfiguration(
    id: string,
    input: Partial<WatchdogConfigurationInput>,
  ): Promise<WatchdogConfiguration> {
    const backendInput = this.mapConfigurationToBackend(input);
    const response = await super.put<ConfigurationSingleApiResponse>(
      `${api.watchdog_configurations}/${id}`,
      backendInput,
    );

    // Check for API-level validation errors
    if (!response.success || !response.configuration) {
      throw new Error(response.message || 'Failed to update configuration');
    }

    return this.mapConfigurationFromBackend(response.configuration);
  }

  /**
   * Delete a configuration (soft-delete)
   */
  public async deleteConfiguration(id: string): Promise<void> {
    await super.deleteWithoutOrgId<void>(
      `${api.watchdog_configurations}/${id}`,
    );
  }

  // ============================================
  // XSLT TEMPLATE ENDPOINTS
  // ============================================

  /**
   * Get all XSLT templates for the organization
   */
  public async getTemplates(): Promise<XsltTemplate[]> {
    const response = await super.get<void, TemplateListApiResponse>(
      api.watchdog_templates,
    );
    return response.templates || [];
  }

  /**
   * Get a single XSLT template with content
   */
  public async getTemplate(id: string): Promise<XsltTemplate> {
    const response = await super.get<void, TemplateSingleApiResponse>(
      `${api.watchdog_templates}/${id}`,
    );
    return response.template;
  }

  /**
   * Create a new XSLT template
   */
  public async createTemplate(input: XsltTemplateInput): Promise<XsltTemplate> {
    const response = await super.post<TemplateSingleApiResponse>(
      api.watchdog_templates,
      input,
    );

    // Check for API-level errors
    if (!response.success || !response.template) {
      throw new Error(response.message || 'Failed to create template');
    }

    return response.template;
  }

  /**
   * Update an existing XSLT template
   */
  public async updateTemplate(
    id: string,
    input: Partial<XsltTemplateInput>,
  ): Promise<XsltTemplate> {
    const response = await super.put<TemplateSingleApiResponse>(
      `${api.watchdog_templates}/${id}`,
      input,
    );

    // Check for API-level errors
    if (!response.success || !response.template) {
      throw new Error(response.message || 'Failed to update template');
    }

    return response.template;
  }

  /**
   * Delete an XSLT template (soft-delete)
   */
  public async deleteTemplate(id: string): Promise<void> {
    await super.deleteWithoutOrgId<void>(`${api.watchdog_templates}/${id}`);
  }

  // ============================================
  // STATUS ENDPOINT
  // ============================================

  /**
   * Get WatchDog service status
   */
  public async getStatus(): Promise<WatchdogStatus> {
    return super.get<void, WatchdogStatus>(api.watchdog_status);
  }

  // ============================================
  // RELEASES ENDPOINT
  // ============================================

  /**
   * Get latest WatchDog release version for current environment
   */
  public async getLatestRelease(): Promise<{ version: string; published_at?: string; download_url?: string }> {
    const response = await super.getWithoutOrgId<{
      success: boolean;
      version: string;
      published_at?: string;
      download_url?: string;
    }>(api.watchdog_releases);

    if (!response.success || !response.version) {
      throw new Error('Failed to fetch latest release');
    }

    return {
      version: response.version,
      published_at: response.published_at,
      download_url: response.download_url,
    };
  }

  // ============================================
  // V2 CONFIG IMPORT
  // ============================================

  /**
   * Import a legacy V2 watchdog-config.json and convert to V3 configurations.
   * Creates one configuration per doc_type in the V2 config.
   */
  public async importV2Config(
    v2Config: V2ConfigSchema,
  ): Promise<{ configurations: WatchdogConfiguration[]; message?: string; created_count: number }> {
    const response = await super.post<V2ConfigImportApiResponse>(
      `${api.watchdog_configurations}/import-v2`,
      { v2_config: v2Config },
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to import V2 configuration');
    }

    return {
      configurations: this.mapConfigurationsFromBackend(response.configurations || []),
      message: response.message,
      created_count: response.created_count,
    };
  }

  // ============================================
  // XSLT VALIDATION ENDPOINTS
  // ============================================

  /**
   * Validate XSLT template against sample XML for a document type.
   * Tests syntax, transformation, and field compatibility.
   */
  public async validateXsltTemplate(
    request: XsltValidationRequest,
  ): Promise<XsltValidationResponse> {
    const response = await super.post<XsltValidationResponse>(
      api.xslt_validate,
      request,
    );
    return response;
  }

  /**
   * Get list of supported document types for XSLT validation.
   * Returns types with their field definitions status.
   */
  public async getXsltDocTypes(): Promise<XsltDocType[]> {
    const response = await super.get<void, { doc_types: XsltDocType[] }>(
      api.xslt_doc_types,
    );
    return response.doc_types || [];
  }

  // ============================================
  // FIELD MAPPING HELPERS
  // ============================================

  /**
   * Map backend export_destination array to frontend export_type
   */
  private mapExportDestinationToType(
    exportDest: string[] | undefined,
  ): WatchdogExportType {
    const dest = exportDest?.[0];
    if (!dest) return 'file';
    // Backend uses 'folder' for file export
    if (dest === 'folder') return 'file';
    // Direct mapping for other types
    if (['infor', 'rest_api', 'gls840mi', 'webhook'].includes(dest)) {
      return dest as WatchdogExportType;
    }
    return 'file';
  }

  /**
   * Map frontend export_type to backend export_destination array
   */
  private mapExportTypeToDestination(
    exportType: WatchdogExportType | undefined,
  ): string[] {
    if (!exportType) return ['folder'];
    // Frontend uses 'file', backend uses 'folder'
    if (exportType === 'file') return ['folder'];
    return [exportType];
  }

  /**
   * Map backend configuration to frontend format
   */
  private mapConfigurationFromBackend(
    backend: BackendConfiguration,
  ): WatchdogConfiguration {
    return {
      id: backend.id,
      name: backend.name,
      config_type: (backend.config_type || 'export') as ConfigType,
      sub_org_id: backend.sub_org_id,
      sub_org_name: backend.sub_org_name,
      doc_type_key: backend.doc_type,
      doc_type_name: backend.doc_type_name,
      import_path: backend.watch_folder,
      success_path: backend.success_folder,
      export_path: backend.export_folder,
      xslt_template_id: backend.xslt_template_id,
      xslt_template_name: backend.xslt_template_name,
      export_type: this.mapExportDestinationToType(backend.export_destination),
      export_config_id: backend.export_config_id,
      export_config_name: backend.export_config_name,
      // REST API fields
      rest_api_url: backend.rest_api_url,
      method: backend.method as 'POST' | 'PUT' | 'GET' | undefined,
      username: backend.username,
      password: backend.password,
      body_type: backend.body_type,
      rest_api_mapping: backend.rest_api_mapping,
      // GLS840MI fields
      endpoint_name: backend.endpoint_name,
      // Webhook fields
      webhook_url: backend.webhook_url,
      // BOD mapping
      bod_mapping: backend.bod_mapping,
      is_default: backend.is_default,
      is_active: backend.enabled,
      created_at: backend.created_at,
      updated_at: backend.updated_at,
    };
  }

  /**
   * Map array of backend configurations to frontend format
   */
  private mapConfigurationsFromBackend(
    backends: BackendConfiguration[],
  ): WatchdogConfiguration[] {
    return backends.map((b) => this.mapConfigurationFromBackend(b));
  }

  /**
   * Map frontend configuration input to backend format
   */
  private mapConfigurationToBackend(
    input: Partial<WatchdogConfigurationInput>,
  ): Record<string, unknown> {
    const result: Record<string, unknown> = {};

    if (input.name !== undefined) result.name = input.name;
    if (input.config_type !== undefined) result.config_type = input.config_type;
    if (input.sub_org_id !== undefined) result.sub_org_id = input.sub_org_id;
    if (input.doc_type_key !== undefined) result.doc_type = input.doc_type_key;
    if (input.import_path !== undefined) result.watch_folder = input.import_path;
    if (input.success_path !== undefined)
      result.success_folder = input.success_path;
    if (input.export_path !== undefined)
      result.export_folder = input.export_path;
    if (input.xslt_template_id !== undefined)
      result.xslt_template_id = input.xslt_template_id;

    // Export type -> export_destination array
    if (input.export_type !== undefined) {
      result.export_destination = this.mapExportTypeToDestination(input.export_type);
    }

    // Export config ID (for Infor and GLS840MI)
    if (input.export_config_id !== undefined)
      result.export_config_id = input.export_config_id;

    // REST API fields
    if (input.rest_api_url !== undefined) result.rest_api_url = input.rest_api_url;
    if (input.method !== undefined) result.method = input.method;
    if (input.username !== undefined) result.username = input.username;
    if (input.password !== undefined) result.password = input.password;
    if (input.body_type !== undefined) result.body_type = input.body_type;
    if (input.rest_api_mapping !== undefined)
      result.rest_api_mapping = input.rest_api_mapping;

    // GLS840MI fields
    if (input.endpoint_name !== undefined) result.endpoint_name = input.endpoint_name;

    // Webhook fields
    if (input.webhook_url !== undefined) result.webhook_url = input.webhook_url;

    // BOD mapping
    if (input.bod_mapping !== undefined) result.bod_mapping = input.bod_mapping;

    if (input.is_default !== undefined) result.is_default = input.is_default;
    if (input.is_active !== undefined) result.enabled = input.is_active;

    return result;
  }
}
